import type { PaletteMode } from '@mui/material';

import { getUrlAppName, parseJSON } from '/helpers';
import { preferredThemeModeLocalStorageKey } from '/constants/themeConstants';

import { startAppListening } from '../../listener';
import { rehydrate } from '../auth/slice';

import { uiToolkit } from './slice';
import type { IUIState } from './state';
import { uiInitialState } from './state';

function getInitUIState(userID: string): IUIState {
  const persistedThemeModelessUI: string | null = localStorage.getItem(`ui:${userID}`);
  const persistedThemeMode = localStorage.getItem(preferredThemeModeLocalStorageKey) as PaletteMode;

  if (!persistedThemeModelessUI) {
    return uiInitialState;
  }

  try {
    const uiState = parseJSON<undefined | IUIState>(persistedThemeModelessUI);
    if (!uiState) {
      return uiInitialState;
    }

    uiState.currentThemeMode = persistedThemeMode;
    return uiState;
  } catch (e) {
    console.error(`Can't read ui state from localStorage`, e);

    return uiInitialState;
  }
}

export const initListeners = () => {
  startAppListening({
    actionCreator: rehydrate,
    effect: async (action, api) => {
      const urlAppName = getUrlAppName(window.location.pathname);
      const isLoginPage = window.location.pathname.includes('login');

      const userID = action.payload.user.id;
      const uiState = getInitUIState(userID);

      /**
       * `getInitUIState` may return the Immer-frozen `uiInitialState` (when there
       * is no persisted UI state, e.g. a fresh browser or e2e run), so build the
       * payload immutably instead of writing in place. A direct mutation threw
       * `TypeError: Cannot assign to read only property 'open'` and broke bootstrap.
       *
       * Always reset the transient `openAsPage` flag: a stale persisted `true` would leave
       * the Header timeline button permanently disabled. `TimelinePage`'s mount effect
       * re-sets it if the app actually loads onto the timeline route.
       */
      const nextUIState: IUIState = {
        ...uiState,
        timeline: {
          ...uiState.timeline,
          openAsPage: false,
          ...(!isLoginPage && !urlAppName ? { open: false } : {}),
        },
      };

      api.dispatch(uiToolkit.actions.update(nextUIState));
    },
  });
};
